import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Shield, ArrowLeft } from "lucide-react";
import { RegisterFields } from "@/components/auth/LoginForms";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ email: "", password: "", full_name: "", organization: "", role: "attorney" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(f.email, f.password);
      else await register(f);
      nav("/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-5">
      <div className="hidden md:block md:col-span-2 bg-[#0A192F] text-white p-12 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#B91C1C]" />
        <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-[12px] uppercase tracking-widest mb-16" data-testid="back-to-site"><ArrowLeft className="w-4 h-4" /> Back to site</Link>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 relative overflow-hidden border border-white">
            <div className="absolute inset-0 bg-[#0A192F]" /><div className="absolute bottom-0 left-0 right-0 h-1/3 bg-[#B91C1C]" />
            <Shield className="absolute inset-0 m-auto w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="font-serif font-bold">Integrity First</div>
        </div>
        <h1 className="font-serif text-3xl font-bold mb-3 leading-tight">Secure staff & client portal.</h1>
        <p className="text-slate-300 leading-relaxed">Encrypted document exchange, medical chronology, RN-validated analysis, and comprehensive audit logging.</p>
        <div className="mt-10 space-y-2 text-[12px] text-slate-400 font-mono uppercase tracking-widest">
          <div>· HIPAA-Aligned</div><div>· MFA-Ready</div><div>· Auditable</div>
        </div>
      </div>
      <div className="md:col-span-3 grid place-items-center p-6 md:p-16 bg-white">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="text-caption text-[#B91C1C] mb-3">{mode === "login" ? "SIGN IN" : "CREATE ACCOUNT"}</div>
          <h2 className="font-serif text-3xl font-bold text-[#0A192F] mb-6">{mode === "login" ? "Portal access." : "Request portal access."}</h2>
          {mode === "register" && <RegisterFields f={f} set={set} />}
          <div className="mb-3"><label className="text-caption block mb-1.5">Email</label><input required type="email" value={f.email} onChange={set("email")} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#0A192F]" data-testid="login-email" /></div>
          <div className="mb-6"><label className="text-caption block mb-1.5">Password</label><input required type="password" value={f.password} onChange={set("password")} minLength={8} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#0A192F]" data-testid="login-password" /></div>
          <button disabled={busy} className="btn-navy w-full py-3 text-[12px] uppercase tracking-widest disabled:opacity-50" data-testid="login-submit">{busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}</button>
          <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-4 w-full text-[12px] text-slate-600 hover:text-[#0A192F] uppercase tracking-widest" data-testid="toggle-mode">
            {mode === "login" ? "Need an account? Register" : "Already have an account? Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
